import { apiClient } from './api';

// VERACODE-MITIGATION: CWE-918 - All API requests use the environment-configured base URL
// (VITE_API_BASE_URL) via the shared API client. URLs are not user-controlled.

// Type for pagination metadata from API responses
interface ApiMeta {
  total?: number;
  page?: number;
  limit?: number;
  totalPages?: number;
}

export interface SubjectInformation {
  name?: string | null;
  email?: string | null;
  phone?: string | null;
  role?: string | null;
  department?: string | null;
  notes?: string | null;
}

// New: Investigation Subject for multiple subjects support
export interface InvestigationSubject {
  id?: string;
  name?: string | null;
  email?: string | null;
  phone?: string | null;
  role?: string | null;
  department?: string | null;
  notes?: string | null;
  isPrimary?: boolean;
}

export interface InvestigationAttachment {
  id: string;
  investigationId: string;
  evidenceType: 'DOCUMENT' | 'PHOTO' | 'VIDEO' | 'AUDIO' | 'OTHER';
  filename: string;
  originalFilename: string;
  fileSize: number;
  mimeType: string;
  filePath: string;
  description?: string;
  uploadedBy: string;
  uploadedAt: string;
  virusScanStatus: 'PENDING' | 'CLEAN' | 'INFECTED';
  virusScanResult?: string;
  isConfidential: boolean;
}

export interface Investigation {
  id: string;
  title: string;
  narrative: string;
  caseNumber: string;
  status: 'ACTIVE' | 'PENDING' | 'CLOSED' | 'SUSPENDED';
  priority: 'LOW' | 'MEDIUM' | 'HIGH' | 'CRITICAL';
  category: string;
  subjects?: InvestigationSubject[];  // New: array of subjects
  subjectInformation?: SubjectInformation; // Backward compatibility
  leadInvestigator: {
    id: string;
    firstName: string;
    lastName: string;
    email: string;
  } | null;
  assignedTo: Array<{
    id: string;
    firstName: string;
    lastName: string;
    email: string;
  }>;
  createdAt: string;
  updatedAt: string;
  dueDate?: string;
  estimatedCompletionDate?: string;
  evidence: {
    documentsCount: number;
    mediaCount: number;
    witnessStatements: number;
  };
  relatedCases: string[];
  correlatedData: {
    incidentIds: string[];
    threatIds: string[];
    personIds: string[];
  };
  timeline?: TimelineEvent[];
  notes?: InvestigationNote[];
  attachments?: InvestigationAttachment[];
  // Public intake fields — populated when source === 'PUBLIC_INTAKE'.
  // Defaults to 'INTERNAL' for legacy/staff-created investigations.
  source?: 'INTERNAL' | 'PUBLIC_INTAKE';
  referenceNumber?: string | null;
  investigationType?: string | null;
  urgency?: 'STANDARD' | 'PRIORITY' | 'URGENT' | null;
  howHeardAboutUs?: string | null;
  desiredOutcome?: string | null;
  requesterName?: string | null;
  requesterEmail?: string | null;
  requesterPhone?: string | null;
  requesterOrganization?: string | null;
  requesterRole?: string | null;
  subjectType?: 'PERSON' | 'ENTITY' | null;
  subjectAddress?: string | null;
  subjectDob?: string | null;
  subjectIdentifiers?: string | null;
}

export interface TimelineEvent {
  id: string;
  investigationId: string;
  type: 'CREATED' | 'ASSIGNED' | 'EVIDENCE_ADDED' | 'STATUS_CHANGED' | 'NOTE_ADDED' | 'CORRELATION_FOUND' | 'COMPLETED' | 'LEAD_INVESTIGATOR_CHANGED';
  title: string;
  description: string;
  timestamp: string;
  userId: string;
  user: {
    firstName: string;
    lastName: string;
    email: string;
  };
  metadata?: Record<string, unknown>;
}

export interface InvestigationNote {
  id: string;
  investigationId: string;
  content: string;
  isConfidential: boolean;
  createdAt: string;
  updatedAt: string;
  authorId: string;
  author: {
    firstName: string;
    lastName: string;
    email: string;
  };
  attachments?: string[];
}

export interface InvestigationStats {
  total: number;
  active: number;
  pending: number;
  closed: number;
  critical: number;
  byCategory: Record<string, number>;
  byStatus: Record<string, number>;
  byPriority: Record<string, number>;
  averageResolutionTime: number;
  openCasesOverdue: number;
}

export interface CreateFromIncidentData {
  sourceIncidentId: string;
  title: string;
  narrative: string;
  category: string;
  urgency: 'STANDARD' | 'PRIORITY' | 'URGENT';
  investigationType?: string;
  subjectName?: string;
  subjectType?: 'PERSON' | 'ENTITY';
  subjectAddress?: string;
  subjectDob?: string;
  subjectIdentifiers?: string;
  requesterName: string;
  requesterEmail: string;
  requesterPhone?: string;
  requesterOrganization?: string;
  editedFields: string[];
}

export interface CreateInvestigationData {
  title: string;
  narrative: string;
  priority?: 'LOW' | 'MEDIUM' | 'HIGH' | 'CRITICAL';
  category?: string;
  leadInvestigator?: {
    id: string;
    firstName: string;
    lastName: string;
    email: string;
  };
  subjects?: InvestigationSubject[];  // New: array of subjects
  subjectInformation?: SubjectInformation; // Backward compatibility
  assignedTo?: string[];
  dueDate?: string;
  relatedCases?: string[];
}

export interface UpdateInvestigationData {
  title?: string;
  narrative?: string;
  status?: 'ACTIVE' | 'PENDING' | 'CLOSED' | 'SUSPENDED';
  priority?: 'LOW' | 'MEDIUM' | 'HIGH' | 'CRITICAL';
  category?: string;
  leadInvestigator?: {
    id: string;
    firstName: string;
    lastName: string;
    email: string;
  };
  subjects?: InvestigationSubject[];  // New: array of subjects
  subjectInformation?: SubjectInformation; // Backward compatibility
  assignedTo?: string[];
  dueDate?: string;
  estimatedCompletionDate?: string;
  user?: {
    id: string;
    firstName: string;
    lastName: string;
    email: string;
  };
}

export interface GetInvestigationsParams {
  page?: number;
  limit?: number;
  status?: string;
  priority?: string;
  category?: string;
  search?: string;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
  leadInvestigatorId?: string;
  assignedToId?: string;
}

export interface DataCorrelation {
  id: string;
  sourceType: 'INCIDENT' | 'THREAT' | 'INVESTIGATION' | 'PERSON';
  sourceId: string;
  targetType: 'INCIDENT' | 'THREAT' | 'INVESTIGATION' | 'PERSON';
  targetId: string;
  correlationType: 'RELATED' | 'CAUSAL' | 'TEMPORAL' | 'GEOGRAPHIC' | 'PERSONNEL';
  confidence: number; // 0-100
  description: string;
  discoveredAt: string;
  discoveredBy: string;
  metadata?: Record<string, unknown>;
}

export interface ApiResponse<T> {
  success: boolean;
  data: T;
  meta: {
    timestamp: string;
    total?: number;
    page?: number;
    limit?: number;
    totalPages?: number;
  };
  error?: {
    code: string;
    message: string;
  };
}

class InvestigationsService {
  /**
   * Get all investigations with filtering and pagination
   */
  async getInvestigations(params: GetInvestigationsParams = {}): Promise<{
    investigations: Investigation[];
    total: number;
    page: number;
    limit: number;
    totalPages: number;
  }> {
    const queryParams = new URLSearchParams();

    if (params.page) { queryParams.append('page', params.page.toString()); }
    if (params.limit) { queryParams.append('limit', params.limit.toString()); }
    if (params.status && params.status !== 'all') { queryParams.append('status', params.status); }
    if (params.priority && params.priority !== 'all') { queryParams.append('priority', params.priority); }
    if (params.category && params.category !== 'all') { queryParams.append('category', params.category); }
    if (params.search) { queryParams.append('search', params.search); }
    if (params.sortBy) { queryParams.append('sortBy', params.sortBy); }
    if (params.sortOrder) { queryParams.append('sortOrder', params.sortOrder); }
    if (params.leadInvestigatorId) { queryParams.append('leadInvestigatorId', params.leadInvestigatorId); }
    if (params.assignedToId) { queryParams.append('assignedToId', params.assignedToId); }

    const url = `/api/v1/investigations${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;
    const response = await apiClient.get<Investigation[]>(url);

    if (response.success && response.data) {
      return {
        investigations: response.data,
        total: (response.meta as ApiMeta)?.total ?? 0,
        page: (response.meta as ApiMeta)?.page ?? 1,
        limit: (response.meta as ApiMeta)?.limit ?? 10,
        totalPages: (response.meta as ApiMeta)?.totalPages ?? 1
      };
    }
    throw new Error(response.error?.message ?? 'Failed to fetch investigations');
  }

  /**
   * Get investigation by ID
   */
  async getInvestigationById(id: string): Promise<Investigation> {
    const response = await apiClient.get<Investigation>(`/api/v1/investigations/${id}`);
    if (response.success && response.data) {
      return response.data;
    }
    throw new Error(response.error?.message ?? 'Failed to fetch investigation');
  }

  /**
   * Create new investigation
   */
  async createInvestigation(data: CreateInvestigationData): Promise<Investigation> {
    const response = await apiClient.post<Investigation>('/api/v1/investigations', data);
    if (response.success && response.data) {
      return response.data;
    }
    throw new Error(response.error?.message ?? 'Failed to create investigation');
  }

  /**
   * Create a new investigation seeded from an existing incident.
   * Throws an Error with `.code` attached so callers can branch on the
   * server-side error code (e.g., INCIDENT_CLOSED) without re-walking the
   * apiClient response shape.
   */
  async createFromIncident(data: CreateFromIncidentData): Promise<Investigation> {
    const response = await apiClient.post<Investigation>('/api/v1/investigations/from-incident', data);
    if (response.success && response.data) {
      return response.data;
    }
    const err = new Error(response.error?.message ?? 'Failed to create investigation from incident');
    (err as Error & { code?: string }).code = response.error?.code;
    throw err;
  }

  /**
   * Update investigation
   */
  async updateInvestigation(id: string, data: UpdateInvestigationData): Promise<Investigation> {
    const response = await apiClient.put<Investigation>(`/api/v1/investigations/${id}`, data);
    if (response.success && response.data) {
      return response.data;
    }
    throw new Error(response.error?.message ?? 'Failed to update investigation');
  }

  /**
   * Delete investigation
   */
  async deleteInvestigation(id: string): Promise<void> {
    const response = await apiClient.delete<void>(`/api/v1/investigations/${id}`);
    if (!response.success) {
      throw new Error(response.error?.message ?? 'Failed to delete investigation');
    }
  }

  /**
   * Get investigation statistics
   */
  async getInvestigationStats(): Promise<InvestigationStats> {
    const response = await apiClient.get<InvestigationStats>('/api/v1/investigations/stats');
    if (response.success && response.data) {
      return response.data;
    }
    throw new Error(response.error?.message ?? 'Failed to fetch investigation statistics');
  }

  /**
   * Get investigation timeline
   */
  async getInvestigationTimeline(id: string): Promise<TimelineEvent[]> {
    const response = await apiClient.get<TimelineEvent[]>(`/api/v1/investigations/${id}/timeline`);
    if (response.success && response.data) {
      return response.data;
    }
    throw new Error(response.error?.message ?? 'Failed to fetch investigation timeline');
  }

  /**
   * Add timeline event
   */
  async addTimelineEvent(investigationId: string, event: Omit<TimelineEvent, 'id' | 'investigationId' | 'timestamp' | 'userId'> & {
    user: {
      id: string;
      firstName: string;
      lastName: string;
      email: string;
    };
  }): Promise<TimelineEvent> {
    const response = await apiClient.post<TimelineEvent>(`/api/v1/investigations/${investigationId}/timeline`, event);
    if (response.success && response.data) {
      return response.data;
    }
    throw new Error(response.error?.message ?? 'Failed to add timeline event');
  }

  /**
   * Get investigation notes
   */
  async getInvestigationNotes(id: string): Promise<InvestigationNote[]> {
    const response = await apiClient.get<InvestigationNote[]>(`/api/v1/investigations/${id}/notes`);
    if (response.success && response.data) {
      return response.data;
    }
    throw new Error(response.error?.message ?? 'Failed to fetch investigation notes');
  }

  /**
   * Add investigation note
   */
  async addInvestigationNote(investigationId: string, note: {
    content: string;
    isConfidential?: boolean;
    author: {
      id: string;
      firstName: string;
      lastName: string;
      email: string;
    };
  }): Promise<InvestigationNote> {
    const response = await apiClient.post<InvestigationNote>(`/api/v1/investigations/${investigationId}/notes`, note);
    if (response.success && response.data) {
      return response.data;
    }
    throw new Error(response.error?.message ?? 'Failed to add investigation note');
  }

  /**
   * Get data correlations for investigation
   */
  async getDataCorrelations(investigationId: string): Promise<DataCorrelation[]> {
    const response = await apiClient.get<DataCorrelation[]>(`/api/v1/investigations/${investigationId}/correlations`);
    if (response.success && response.data) {
      return response.data;
    }
    throw new Error(response.error?.message ?? 'Failed to fetch data correlations');
  }

  /**
   * Add data correlation
   */
  async addDataCorrelation(investigationId: string, correlation: Omit<DataCorrelation, 'id' | 'discoveredAt' | 'discoveredBy'>): Promise<DataCorrelation> {
    const response = await apiClient.post<DataCorrelation>(`/api/v1/investigations/${investigationId}/correlations`, correlation);
    if (response.success && response.data) {
      return response.data;
    }
    throw new Error(response.error?.message ?? 'Failed to add data correlation');
  }

  /**
   * Get status display properties
   */
  getStatusConfig(status: string) {
    const config = {
      ACTIVE: { color: 'success', label: 'Active' },
      PENDING: { color: 'warning', label: 'Pending' },
      CLOSED: { color: 'secondary', label: 'Closed' },
      SUSPENDED: { color: 'danger', label: 'Suspended' }
    };
    return config[status as keyof typeof config] ?? { color: 'secondary', label: status };
  }

  /**
   * Get priority display properties
   */
  getPriorityConfig(priority: string) {
    const config = {
      CRITICAL: { color: 'danger', label: 'Critical' },
      HIGH: { color: 'warning', label: 'High' },
      MEDIUM: { color: 'primary', label: 'Medium' },
      LOW: { color: 'success', label: 'Low' }
    };
    return config[priority as keyof typeof config] ?? { color: 'secondary', label: priority };
  }

  /**
   * Format timestamp for display
   */
  formatTimestamp(timestamp: string): string {
    const date = new Date(timestamp);
    const now = new Date();
    const diffInMinutes = Math.floor((now.getTime() - date.getTime()) / (1000 * 60));

    if (diffInMinutes < 1) { return 'Just now'; }
    if (diffInMinutes < 60) { return `${diffInMinutes} minutes ago`; }

    const diffInHours = Math.floor(diffInMinutes / 60);
    if (diffInHours < 24) { return `${diffInHours} hours ago`; }

    const diffInDays = Math.floor(diffInHours / 24);
    if (diffInDays < 30) { return `${diffInDays} days ago`; }

    return date.toLocaleDateString();
  }

  /**
   * Get user display name
   */
  getUserDisplayName(user?: { firstName: string; lastName: string }) {
    if (!user) { return 'Unassigned'; }
    return `${user.firstName} ${user.lastName}`;
  }

  /**
   * Generate case number
   */
  generateCaseNumber(): string {
    const year = new Date().getFullYear();
    const timestamp = Date.now().toString().slice(-6);
    return `INV-${year}-${timestamp}`;
  }

  /**
   * Get attachments for an investigation
   */
  async getAttachments(investigationId: string): Promise<InvestigationAttachment[]> {
    try {
      const response = await apiClient.get<InvestigationAttachment[]>(
        `/api/v1/investigations/${investigationId}/attachments`
      );
      return response.data ?? [];
    } catch (error) {
      console.error('Failed to fetch attachments:', error);
      throw error;
    }
  }

  /**
   * Upload attachment to investigation
   */
  async uploadAttachment(
    investigationId: string,
    file: File,
    description?: string,
    evidenceType: 'DOCUMENT' | 'PHOTO' | 'VIDEO' | 'AUDIO' | 'OTHER' = 'DOCUMENT',
    onProgress?: (progress: number) => void
  ): Promise<InvestigationAttachment> {
    const additionalData: Record<string, unknown> = { evidenceType };
    if (description) {
      additionalData.description = description;
    }

    const response = await apiClient.uploadFile<InvestigationAttachment>(
      `/api/v1/investigations/${investigationId}/attachments`,
      file,
      onProgress,
      additionalData
    );

    if (response.success && response.data) {
      return response.data;
    }
    throw new Error(response.error?.message ?? 'Failed to upload attachment');
  }

  /**
   * Delete an attachment
   */
  async deleteAttachment(attachmentId: string): Promise<void> {
    try {
      await apiClient.delete(`/api/v1/investigations/attachments/${attachmentId}`);
    } catch (error) {
      console.error('Failed to delete attachment:', error);
      throw error;
    }
  }

  /**
   * Download an attachment - returns full URL with auth token
   */
  async downloadAttachment(attachmentId: string): Promise<void> {
    try {
      const token = localStorage.getItem('corp_security_token');
      const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || '';
      const url = `${API_BASE_URL}/api/v1/investigations/attachments/${attachmentId}/download`;

      // Use fetch to download with authentication
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error(`Download failed: ${response.statusText}`);
      }

      // Get filename from Content-Disposition header or use default
      const contentDisposition = response.headers.get('Content-Disposition');
      let filename = `attachment-${attachmentId}`;

      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename="?(.+)"?/i);
        if (filenameMatch?.[1]) {
          filename = filenameMatch[1].replace(/['"]/g, '');
        }
      }

      // Create blob and download
      const blob = await response.blob();
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(downloadUrl);
    } catch (error) {
      console.error('Failed to download attachment:', error);
      throw error;
    }
  }

  /**
   * Get download URL for attachment (for previews and direct links)
   */
  getAttachmentUrl(attachmentId: string): string {
    const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || '';
    return `${API_BASE_URL}/api/v1/investigations/attachments/${attachmentId}/download`;
  }

  /**
   * Add a subject to an investigation
   */
  async addSubject(investigationId: string, subject: InvestigationSubject): Promise<InvestigationSubject> {
    const response = await apiClient.post<InvestigationSubject>(
      `/api/v1/investigations/${investigationId}/subjects`,
      subject
    );
    if (response.success && response.data) {
      return response.data;
    }
    throw new Error(response.error?.message ?? 'Failed to add subject');
  }

  /**
   * Update a subject
   */
  async updateSubject(subjectId: string, subject: Partial<InvestigationSubject>): Promise<InvestigationSubject> {
    const response = await apiClient.put<InvestigationSubject>(
      `/api/v1/investigations/subjects/${subjectId}`,
      subject
    );
    if (response.success && response.data) {
      return response.data;
    }
    throw new Error(response.error?.message ?? 'Failed to update subject');
  }

  /**
   * Delete a subject
   */
  async deleteSubject(subjectId: string): Promise<void> {
    const response = await apiClient.delete<void>(`/api/v1/investigations/subjects/${subjectId}`);
    if (!response.success) {
      throw new Error(response.error?.message ?? 'Failed to delete subject');
    }
  }

  /**
   * Link an incident to an investigation
   */
  async linkIncident(investigationId: string, incidentId: string): Promise<{ correlatedIncidentIds: string[] }> {
    const response = await apiClient.post<{ correlatedIncidentIds: string[] }>(
      `/api/v1/investigations/${investigationId}/link-incident`,
      { incidentId, action: 'link' }
    );
    if (response.success && response.data) {
      return response.data;
    }
    throw new Error(response.error?.message ?? 'Failed to link incident');
  }

  /**
   * Unlink an incident from an investigation
   */
  async unlinkIncident(investigationId: string, incidentId: string): Promise<{ correlatedIncidentIds: string[] }> {
    const response = await apiClient.post<{ correlatedIncidentIds: string[] }>(
      `/api/v1/investigations/${investigationId}/link-incident`,
      { incidentId, action: 'unlink' }
    );
    if (response.success && response.data) {
      return response.data;
    }
    throw new Error(response.error?.message ?? 'Failed to unlink incident');
  }
}

export const investigationsService = new InvestigationsService();
export default investigationsService;